import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  RightOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Typography } from 'antd';
import { CornerDownRight, ListChecks } from 'lucide-react';
import { resolveProcessLogPresentation } from "./assistantTurnPresentation.jsx";
import ThinkingProcessActivities from './ThinkingProcessActivities.jsx';
import ThinkingProcessResourceLine from './ThinkingProcessResourceLine.jsx';
import WorkflowOperationActions from './WorkflowOperationActions.jsx';

const { Text } = Typography;

const PROCESS_STATUS_LABELS = new Map([
  ['pending', '待执行'],
  ['skipped', '已跳过'],
  ['running', '进行中'],
  ['completed', '已完成'],
  ['partial', '部分完成'],
  ['degraded', '部分完成'],
  ['failed', '失败'],
  ['waiting_user', '等待确认'],
  ['blocked', '已阻塞'],
  ['cancelled', '已取消'],
  ['unavailable', '完成情况待核实'],
]);
const statusIcon = (status = '') => {
  const icons = new Map([
    ['running', <LoadingOutlined spin />],
    ['completed', <CheckOutlined />],
    ['partial', <ExclamationCircleOutlined />],
    ['degraded', <ExclamationCircleOutlined />],
    ['failed', <CloseCircleOutlined />],
    ['waiting_user', <ClockCircleOutlined />],
    ['blocked', <StopOutlined />],
    ['cancelled', <StopOutlined />],
  ]);
  return icons.get(status) ?? <MinusCircleOutlined />;
};
const statusStyleKey = (status = '') => status;
const isResourceLine = (line = '') => line.startsWith('来源 ·');
const isResourceSummaryLine = (line = '') => /^读取 \d+ 份资料$/.test(line);

const accessibleStatusLabel = (label) => label || '状态';
const activityGroupWithStatus = (activityGroup, status) => (
  activityGroup ? { ...activityGroup, stepStatus: status } : null
);

function ProcessDetailLines({ detailLines, itemKey, onUICardAction, resources = [] }) {
  const resourcesByLine = new Map(resources.map((resource) => [resource.line, resource]));
  const resourceSummary = detailLines.find(isResourceSummaryLine) || '';
  const sourceLines = detailLines.filter(isResourceLine);
  const ordinaryLines = detailLines.filter((detail) => (
    !isResourceLine(detail) && !isResourceSummaryLine(detail)
  ));
  return (
    <span className="flare-thinking-process-detail">
      {ordinaryLines.map((detail, detailIndex) => (
        <span
          className="flare-thinking-process-detail-line"
          key={`${itemKey}-detail-${detailIndex}`}
        >
          {detail}
        </span>
      ))}
      {sourceLines.length > 0 ? (
        <span className="flare-thinking-process-resource-group">
          {resourceSummary ? (
            <span className="flare-thinking-process-resource-summary">{resourceSummary}</span>
          ) : null}
          {sourceLines.map((detail, detailIndex) => (
            <ThinkingProcessResourceLine
              fallbackLine={detail}
              key={`${itemKey}-source-${detailIndex}`}
              onUICardAction={onUICardAction}
              resource={resourcesByLine.get(detail)}
            />
          ))}
        </span>
      ) : null}
    </span>
  );
}

const ProcessTitle = ({ title }) => <span className="flare-thinking-process-title">{title}</span>;

export function ProcessCompactSummary({ label, status, summary, actions, statusClassName = 'flare-thinking-status-pill' }) {
  if (!status) return null;
  const compactIcon = new Map(['partial', 'degraded', 'failed', 'blocked', 'cancelled', 'waiting_user', 'waiting']
    .map((key) => [key, status.statusIcon])).get(status.statusKey);
  const titles = [label].filter((title) => title && title !== status.statusLabel);
  return (
    <div className="flare-thinking-process-compact">
      <CornerDownRight aria-hidden="true" size={16} />
      {titles.map((title) => <ProcessTitle key={title} title={title} />)}
      {summary ? <span className="flare-thinking-process-summary">{summary}</span> : null}
      <span className={`${statusClassName} is-${status.statusKey ?? 'unknown'}`}>{compactIcon}{status.statusLabel}</span>
      {actions}
    </div>
  );
}

function ProcessEntryContent({ activityGroup, itemKey, onUICardAction, processLine, resources }) {
  return (
    <>
      <ThinkingProcessActivities group={activityGroup} onUICardAction={onUICardAction} />
      {processLine.detailLines.length > 0
        ? (
          <ProcessDetailLines
            detailLines={processLine.detailLines}
            itemKey={itemKey}
            onUICardAction={onUICardAction}
            resources={resources}
          />
        )
        : null}
    </>
  );
}

function ThinkingProcessItem({
  activityGroup,
  index,
  icon,
  processLine,
  presentation,
  compactStatus,
  onUICardAction,
  onWorkflowOperationAction,
  processActions,
  headerActions,
  compactActions,
  resources,
  stepId,
  themeTokens,
}) {
  const processStatus = processLine.status;
  const processStatusLabel = PROCESS_STATUS_LABELS.get(processStatus) ?? '';
  const processStatusStyleKey = statusStyleKey(processStatus) || 'unknown';
  const statusLabel = accessibleStatusLabel(processStatusLabel);
  const itemKey = stepId || `thinking-process-${index}`;
  const headerControls = <>
    <WorkflowOperationActions actions={headerActions}
      onWorkflowOperationAction={onWorkflowOperationAction} themeTokens={themeTokens} />
    {compactActions}
  </>;
  if (presentation === 'compact') return (
    <li className="flare-thinking-process-item">
      <ProcessCompactSummary label={processLine.title}
        actions={headerControls}
        status={compactStatus ?? { statusKey: processStatusStyleKey, statusLabel: processStatusLabel, statusIcon: statusIcon(processStatus) }}
        statusClassName={compactStatus ? 'flare-thinking-status-pill' : 'flare-thinking-process-status'} />
    </li>
  );
  return (
    <li className="flare-thinking-process-item">
      <details className="flare-thinking-process-step">
        <summary>
          <span aria-hidden="true" className="flare-thinking-process-entry-icon">{icon ?? <ListChecks size={16} />}</span>
          <ProcessTitle title={processLine.title} />
          <span
            aria-label={statusLabel}
            className={`flare-thinking-process-status is-${processStatusStyleKey}`}
            title={statusLabel}
          >
            {statusIcon(processStatus)}
            <span>{processStatusLabel}</span>
          </span>
          {headerControls}
          <RightOutlined aria-hidden="true" className="flare-thinking-process-chevron" />
        </summary>
        <div className="flare-thinking-process-text flare-thinking-process-step-detail">
          <ProcessEntryContent
            activityGroup={activityGroupWithStatus(activityGroup, processLine.status)}
            itemKey={itemKey}
            onUICardAction={onUICardAction}
            processLine={processLine}
            resources={resources}
          />
          <WorkflowOperationActions
            actions={processActions}
            onWorkflowOperationAction={onWorkflowOperationAction}
            themeTokens={themeTokens}
          />
        </div>
      </details>
    </li>
  );
}

function ThinkingProcessList({
  onUICardAction,
  onWorkflowOperationAction,
  themeTokens,
  workflowProcessEntries,
}) {
  return (
    <ul className="flare-thinking-process-list">
      {workflowProcessEntries.map(({ line, stepId = '', icon, processLine, presentation, compactStatus, activityGroup, resources, processActions, headerActions, compactActions }, index) => {
        return (
          <ThinkingProcessItem
            activityGroup={activityGroup}
            index={index}
            icon={icon}
            key={stepId ? `workflow:${stepId}` : `thinking-process-${index}`}
            line={line}
            processLine={processLine}
            presentation={presentation}
            compactStatus={compactStatus}
            onUICardAction={onUICardAction}
            onWorkflowOperationAction={onWorkflowOperationAction}
            processActions={processActions}
            headerActions={headerActions}
            compactActions={compactActions}
            resources={resources}
            stepId={stepId}
            themeTokens={themeTokens}
          />
        );
      })}
    </ul>
  );
}

function ProcessDisclosureSummary({ label, summary, status, actions }) {
  return (
    <summary>
      <ListChecks aria-hidden="true" size={16} />
      <span className="flare-thinking-process-heading">{label}</span>
      {summary ? <span className="flare-thinking-process-summary">{summary}</span> : null}
      <span className={`flare-thinking-status-pill is-${status.statusKey ?? 'unknown'}`}>
        {status.statusIcon}
        <span>{status.statusLabel}</span>
      </span>
      {actions}
      <RightOutlined aria-hidden="true" className="flare-thinking-process-chevron" />
    </summary>
  );
}

function ProcessDisclosure({ processCompleted, shouldOpenProcess, compact, children }) {
  const [open, setOpen] = useState(shouldOpenProcess);
  const previousCompleted = useRef(processCompleted);
  useLayoutEffect(() => {
    const completedNow = processCompleted && !previousCompleted.current;
    previousCompleted.current = processCompleted;
    if (completedNow) setOpen(false);
  }, [processCompleted]);
  return compact ?? <details className="flare-thinking-process-disclosure" onToggle={(event) => setOpen(event.currentTarget.open)} open={open}>{children}</details>;
}

function ProcessDisclosureBody({ hasProcessLines, listProps, themeTokens }) {
  return hasProcessLines ? (
    <ThinkingProcessList {...listProps} />
  ) : (
    <Text type="secondary" style={{ color: themeTokens.textSecondary, fontSize: 12 }}>
      暂无过程详情，正在持续更新…
    </Text>
  );
}

export default function ThinkingProcessLog({
  activityEntries = [],
  hasProcessLines,
  processDisclosureLabel,
  processSummary,
  processStatus = {},
  processActions,
  processCompleted = false,
  resourceEntries = [],
  shouldOpenProcess,
  themeTokens,
  visibleProcessLines,
  workflowOperationActions = [],
  workflowProcessEntries = [],
  onUICardAction,
  onWorkflowOperationAction,
}) {
  const prepared = resolveProcessLogPresentation({
    activityEntries, resourceEntries, workflowOperationActions, onWorkflowOperationAction, processStatus,
    workflowProcessEntries: workflowProcessEntries.slice(0, visibleProcessLines.length),
  });
  const actionSlots = new Map([
    ['compact', new Map([[prepared.entries.at(-1), processActions]])],
    ['details', new Map()],
  ]).get(prepared.presentation);
  const listProps = { onUICardAction, onWorkflowOperationAction,
    themeTokens, workflowProcessEntries: prepared.entries.map((entry) => ({ ...entry, compactActions: actionSlots.get(entry) })) };
  const compact = new Map([['compact', <div className="flare-thinking-process-compact-log">
      <ProcessCompactSummary label={processDisclosureLabel} status={prepared.compactRunStatus} />
      <ThinkingProcessList {...listProps} />
      {processSummary ? <span className="flare-thinking-process-summary">{processSummary}</span> : null}
    </div>]]).get(prepared.presentation);
  return (
    <ProcessDisclosure compact={compact} processCompleted={processCompleted} shouldOpenProcess={shouldOpenProcess}>
      <ProcessDisclosureSummary label={processDisclosureLabel} summary={processSummary} status={processStatus} actions={processActions} />
      <ProcessDisclosureBody
        hasProcessLines={hasProcessLines}
        listProps={listProps}
        themeTokens={themeTokens}
      />
    </ProcessDisclosure>
  );
}
